import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, Animated, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import LoadingSpinner from '../components/LoadingSpinner';
import { LinearGradient } from 'expo-linear-gradient';
import useRegister from '../hooks/useRegister';
import { useUiStyle } from '../context/UiStyleContext';
import GradientText from '../components/GradientText';
import { ScreenBackground } from '../components/ThemeGradients';
import ConsentCheckbox from '../components/ConsentCheckbox';
import { useNavigation } from '../context/NavigationContext';

import { isMobile } from '../utils/responsive';
import { lookSheets, useLookStyles, useOriginalPolish, AUTH_FL_OVERRIDES } from './originalLook';

const FONT = Platform.OS === 'web'
  ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif"
  : 'System';

const webTransition = Platform.OS === 'web'
  ? { transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease, background-color 200ms ease, opacity 200ms ease' }
  : {};

export const Input = React.forwardRef(({
  label,
  labelRight,
  placeholder,
  value,
  onChangeText,
  keyboardType = 'default',
  secureTextEntry = false,
  disabled = false,
  onSubmitEditing,
  returnKeyType,
  blurOnSubmit,
  // Floodlight only: autofill hint and error wiring (Classic keeps autocomplete off, as before).
  autoComplete,
  invalid = false,
  describedBy,
}, ref) => {
  const styles = useLookStyles(SHEETS);
  const { isFloodlight } = useUiStyle();
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const handleWebKeyPress = (event) => {
    if (disabled || !onSubmitEditing) return;
    const key = event.nativeEvent?.key ?? event.key;
    if (key !== 'Enter') return;
    event.preventDefault?.();
    onSubmitEditing(event);
  };

  return (
  <View style={styles.inputWrapper}>
    {(label || labelRight) ? (
      <View style={styles.inputLabelRow}>
        {label ? <Text style={styles.inputLabel}>{label}</Text> : <View />}
        {labelRight || null}
      </View>
    ) : null}
    <View
      onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      style={[
        styles.inputContainer,
        secureTextEntry && styles.inputContainerWithToggle,
        isHovered && !isFocused && styles.inputContainerHovered,
        isFocused && styles.inputContainerFocused,
        isFloodlight && invalid && !isFocused && styles.inputContainerInvalid,
      ]}
    >
      <TextInput
        ref={ref}
        placeholder={placeholder}
        placeholderTextColor="rgba(150, 160, 180, 0.4)"
        style={[styles.input, secureTextEntry && styles.inputWithToggle]}
        value={value}
        onChangeText={onChangeText}
        keyboardType={keyboardType}
        secureTextEntry={secureTextEntry && !isPasswordVisible}
        autoCapitalize="none"
        autoComplete={isFloodlight && autoComplete ? autoComplete : 'off'}
        autoCorrect={false}
        {...(isFloodlight ? {
          accessibilityLabel: label || placeholder,
          'aria-invalid': invalid || undefined,
          'aria-describedby': describedBy,
        } : {})}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        editable={!disabled}
        disabled={disabled}
        onSubmitEditing={Platform.OS === 'web' ? undefined : onSubmitEditing}
        onKeyPress={Platform.OS === 'web' ? handleWebKeyPress : undefined}
        returnKeyType={returnKeyType}
        blurOnSubmit={Platform.OS === 'web' && onSubmitEditing ? false : blurOnSubmit}
      />
      {secureTextEntry ? (
        <Pressable
          onPress={() => setIsPasswordVisible((visible) => !visible)}
          disabled={disabled}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={isPasswordVisible ? 'Hide password' : 'Show password'}
          style={({ pressed, hovered }) => [
            styles.passwordToggle,
            (pressed || hovered) && !disabled && styles.passwordToggleActive,
            disabled && styles.passwordToggleDisabled,
          ]}
        >
          <Ionicons
            name={isPasswordVisible ? 'eye-off-outline' : 'eye-outline'}
            size={18}
            color={isPasswordVisible ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.45)'}
          />
        </Pressable>
      ) : null}
    </View>
  </View>
  );
});
Input.displayName = 'Input';

export default function RegisterScreen() {
  const styles = useLookStyles(SHEETS);
  // Floodlight polish; reduced motion skips the entrance and the exit fade (Classic keeps 600ms).
  const { isFloodlight, still, root } = useOriginalPolish();
  const {
    username, handleUsernameChange, usernameStatus,
    email, setEmail, password, setPassword,
    consent, setConsent, consentMissing,
    error, loading, isBusy,
    usernameRef, emailRef, passwordRef, pageOpacity,
    handleRegister, goToLogin,
  } = useRegister({ exitDuration: still ? 0 : 600 });
  const { navigateTo } = useNavigation();

  // Entrance animation values
  const headerAnim = useRef(new Animated.Value(still ? 1 : 0)).current;
  const cardAnim = useRef(new Animated.Value(still ? 1 : 0)).current;

  // Page load animation: header rises in, card follows just behind it (skipped under reduced motion in Floodlight)
  useEffect(() => {
    if (still) return;
    Animated.parallel([
      Animated.timing(headerAnim, { toValue: 1, duration: 600, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.timing(cardAnim, { toValue: 1, duration: 650, delay: 120, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
    ]).start();
  }, []);

  const rise = (anim, from = 14) => ({
    opacity: anim,
    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [from, 0] }) }],
  });

  const cardEntrance = {
    opacity: cardAnim,
    transform: [
      { translateY: cardAnim.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }) },
      { scale: cardAnim.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1] }) },
    ],
  };

  return (
    <View style={styles.container} {...root}>
      <ScreenBackground />
      <Animated.View style={[styles.mainContent, { opacity: pageOpacity }]}>
        <Animated.View style={[styles.headerSection, rise(headerAnim, -12)]}>
          <Text style={styles.brandTitle}>BETOPTIMIZED</Text>
          <Text style={styles.brandTagline}>EVERY BOOK · EVERY MARKET · ONE EDGE</Text>
        </Animated.View>

        <Animated.View style={[styles.formContainer, cardEntrance]}>
          <BlurView intensity={20} tint="dark" style={styles.formCard}>
            <View style={styles.formContent}>
              <View style={styles.titleSection}>
                <GradientText style={styles.welcomeTitle} gradientColors={['#FFFFFF', '#A9C2E8']}>Create your account</GradientText>
                <Text style={styles.welcomeSubtitle}>Join free and start finding your edge in seconds.</Text>
              </View>

              <View style={styles.formSection}>
                <View style={styles.fieldBlock}>
                  <Input
                    ref={usernameRef}
                    label="Username"
                    placeholder="Pick a username"
                    value={username}
                    onChangeText={handleUsernameChange}
                    disabled={isBusy}
                    returnKeyType="next"
                    blurOnSubmit={false}
                    onSubmitEditing={() => emailRef.current?.focus()}
                    autoComplete="username"
                    invalid={usernameStatus === 'taken' || usernameStatus === 'invalid'}
                    describedBy={usernameStatus ? 'register-username-hint' : undefined}
                  />
                  <UsernameHint isFloodlight={isFloodlight}>
                  {usernameStatus === 'available' && (
                    <Text style={styles.usernameAvailable}>✓ Username available</Text>
                  )}
                  {usernameStatus === 'taken' && (
                    <Text style={styles.usernameTaken}>✗ Username already taken</Text>
                  )}
                  {usernameStatus === 'invalid' && (
                    <Text style={styles.usernameTaken}>3+ chars, letters/numbers/underscores only</Text>
                  )}
                  {usernameStatus === 'checking' && (
                    <Text style={styles.usernameChecking}>Checking availability…</Text>
                  )}
                  </UsernameHint>
                </View>
                <Input
                  ref={emailRef}
                  label="Email"
                  placeholder="you@example.com"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  disabled={isBusy}
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => passwordRef.current?.focus()}
                  autoComplete="email"
                />
                <Input
                  ref={passwordRef}
                  label="Password"
                  placeholder="Create a password"
                  value={password}
                  onChangeText={setPassword}
                  autoCapitalize="none"
                  secureTextEntry={true}
                  disabled={isBusy}
                  returnKeyType="go"
                  onSubmitEditing={handleRegister}
                  autoComplete="new-password"
                />

                <ConsentCheckbox checked={consent} onChange={setConsent} disabled={isBusy} navigateTo={navigateTo} invalid={consentMissing} />

                {error ? <Text style={[styles.statusText, styles.statusError]} {...(isFloodlight ? { accessibilityRole: 'alert' } : {})}>{error}</Text> : null}

                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.primaryButton,
                    isBusy && styles.buttonDisabled,
                    hovered && !isBusy && styles.primaryHover,
                    pressed && !isBusy && styles.buttonPressed,
                  ]}
                  onPress={handleRegister}
                  disabled={isBusy}
                  {...(isFloodlight ? { accessibilityRole: 'button', accessibilityLabel: loading ? 'Creating your account' : 'Create account' } : {})}
                >
                  <LinearGradient
                    colors={['rgba(59, 130, 246, 0.32)', 'rgba(30, 80, 220, 0.16)']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.buttonGradient}
                  >
                    {loading ? (
                      <View style={styles.loadingContainer}>
                        <LoadingSpinner size={16} color="rgba(255, 255, 255, 0.9)" />
                        <Text style={styles.primaryButtonText}>Creating your account…</Text>
                      </View>
                    ) : (
                      <Text style={styles.primaryButtonText}>Create Account</Text>
                    )}
                  </LinearGradient>
                </Pressable>
              </View>

              <View style={styles.footerSection}>
                <Pressable
                  onPress={goToLogin}
                  disabled={isBusy}
                  {...(isFloodlight ? { accessibilityRole: 'link', accessibilityLabel: 'Sign in' } : {})}
                  style={({ pressed, hovered }) => [
                    styles.linkPressable,
                    (pressed || hovered) && !isBusy && styles.linkPressableHover,
                  ]}
                >
                  {({ hovered }) => (
                    <Text style={[styles.footerText, isBusy && styles.textDisabled]}>
                      Already have an account?{' '}
                      <Text style={[styles.footerAccent, hovered && !isBusy && styles.footerAccentHover]}>Sign in</Text>
                    </Text>
                  )}
                </Pressable>
              </View>
            </View>
          </BlurView>
        </Animated.View>
      </Animated.View>
    </View>
  );
}

const SHEETS = lookSheets({
  container: {
    flex: 1,
    backgroundColor: '#000000',
    borderWidth: 2,
    borderColor: '#000',
  },
  mainContent: {
    flex: 1,
    paddingHorizontal: isMobile ? 16 : (Platform.OS === 'web' ? 48 : 20),
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: isMobile ? 24 : 32,
    gap: isMobile ? 8 : 10,
  },
  brandTitle: {
    fontFamily: 'ClimateCrisis',
    fontSize: isMobile ? 22 : (Platform.OS === 'web' ? 40 : 28),
    fontWeight: '400',
    lineHeight: isMobile ? 26 : (Platform.OS === 'web' ? 44 : 32),
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  brandTagline: {
    fontFamily: FONT,
    fontSize: isMobile ? 10 : 12,
    fontWeight: '500',
    letterSpacing: 2.5,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.45)',
  },
  formContainer: {
    width: '100%',
    maxWidth: 480,
    justifyContent: 'center',
    alignItems: 'center',
  },
  formCard: {
    width: '100%',
    padding: isMobile ? 24 : 40,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && { boxShadow: '0 24px 64px rgba(0, 0, 0, 0.45)' }),
  },
  formContent: {
    width: '100%',
    gap: isMobile ? 20 : 24,
    alignItems: 'center',
  },
  titleSection: {
    alignItems: 'center',
    gap: 8,
  },
  welcomeTitle: {
    fontFamily: FONT,
    fontSize: isMobile ? 24 : 30,
    fontWeight: '600',
    lineHeight: isMobile ? 30 : 36,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.55)',
  },
  formSection: {
    width: '100%',
    gap: isMobile ? 14 : 16,
  },
  fieldBlock: {
    width: '100%',
  },
  inputWrapper: { width: '100%', gap: 6 },
  inputLabelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  inputLabel: { fontSize: 12, fontWeight: '600', letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' },
  inputContainer: {
    width: '100%',
    padding: isMobile ? 10 : 12,
    borderRadius: 10,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    ...(Platform.OS === 'web' && { transition: 'border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease' }),
  },
  inputContainerWithToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inputContainerHovered: { borderColor: 'rgba(255,255,255,0.25)' },
  inputContainerInvalid: {},
  inputContainerFocused: {
    borderColor: 'rgba(140,175,255,0.6)',
    backgroundColor: 'rgba(59,130,246,0.05)',
    ...(Platform.OS === 'web' && { boxShadow: '0 0 0 3px rgba(59,130,246,0.18)' }),
  },
  input: {
    fontSize: 15,
    color: '#fff',
    padding: 0,
    outlineStyle: 'none',
    ...(Platform.OS === 'web' && {
      transition: 'background-color 5000s ease-in-out 0s',
      '&::placeholder': {
        color: 'rgba(150, 160, 180, 0.4)',
        WebkitTextFillColor: 'rgba(150, 160, 180, 0.4)',
        fontStyle: 'italic',
        fontWeight: '400',
      },
      '&:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
        WebkitTextFillColor: '#fff',
        transition: 'background-color 5000s ease-in-out 0s',
      },
      '&:-webkit-autofill:hover': {
        WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
        WebkitTextFillColor: '#fff',
      },
      '&:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
        WebkitTextFillColor: '#fff',
      },
      '&:-webkit-autofill:active': {
        WebkitBoxShadow: '0 0 0 1000px rgba(0,0,0,0.16) inset',
        WebkitTextFillColor: '#fff',
      }
    })
  },
  inputWithToggle: {
    flex: 1,
    minWidth: 0,
  },
  passwordToggle: {
    padding: 4,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    ...webTransition,
  },
  passwordToggleActive: Platform.OS === 'web'
    ? { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
    : { opacity: 0.85 },
  passwordToggleDisabled: {
    opacity: 0.4,
  },
  primaryButton: {
    width: '100%',
    marginTop: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(120, 160, 255, 0.35)',
    overflow: 'hidden',
    shadowColor: '#1E50DC',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
    ...webTransition,
  },
  primaryHover: Platform.OS === 'web' ? {
    transform: [{ translateY: -1 }],
    borderColor: 'rgba(150, 185, 255, 0.65)',
    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.28)',
  } : {},
  buttonPressed: {
    transform: [{ scale: 0.985 }],
  },
  buttonGradient: {
    paddingVertical: 14,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    fontFamily: FONT,
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    letterSpacing: 0.2,
    color: 'rgba(255, 255, 255, 0.95)',
  },
  footerSection: {
    width: '100%',
    alignItems: 'center',
    gap: 10,
  },
  linkPressable: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    alignSelf: 'center',
    borderRadius: 8,
    ...webTransition,
  },
  linkPressableHover: Platform.OS === 'web'
    ? { backgroundColor: 'rgba(255,255,255,0.05)' }
    : {},
  footerText: {
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  footerAccent: {
    color: 'rgba(150, 185, 255, 0.95)',
    fontWeight: '600',
  },
  footerAccentHover: {
    textDecorationLine: 'underline',
  },
  statusText: {
    width: '100%',
    fontFamily: FONT,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  statusError: {
    color: '#F87171',
    borderColor: 'rgba(234, 84, 85, 0.35)',
    backgroundColor: 'rgba(234, 84, 85, 0.08)',
  },
  usernameAvailable: { fontSize: 12, color: 'rgba(52, 211, 153, 0.9)', marginTop: 6, paddingHorizontal: 2, fontFamily: FONT },
  usernameTaken: { fontSize: 12, color: 'rgba(255, 100, 100, 0.9)', marginTop: 6, paddingHorizontal: 2, fontFamily: FONT },
  usernameChecking: { fontSize: 12, color: 'rgba(255, 255, 255, 0.45)', marginTop: 6, paddingHorizontal: 2, fontFamily: FONT },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  textDisabled: {
    opacity: 0.5,
  },
}, AUTH_FL_OVERRIDES);

// Floodlight wraps the username status in a live region the field points at; Classic renders it bare (unchanged DOM).
const UsernameHint = ({ isFloodlight, children }) => (isFloodlight
  ? <View nativeID="register-username-hint" aria-live="polite">{children}</View>
  : <>{children}</>);
